import { VITE_BASE_URL } from '@/lib/constants';
import { BaseQueryFn, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { authUser, userLogout } from './slices/auth';
import { Mutex } from 'async-mutex';
import { RootState } from './store';
import { captureException } from '@sentry/react';
import { useAuthStore } from '@/store/auth.store';

// Mutex to prevent multiple refresh calls
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: VITE_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
        const accessToken = useAuthStore.getState().token;
        if (accessToken) {
            headers.set('Authorization', `Bearer ${accessToken}`);
        }
        return headers;
    },
});

const baseQueryWithReAuth: BaseQueryFn = async (args, api, extraOptions) => {
    // Wait until the mutex is available without locking it
    await mutex.waitForUnlock();

    let response = await baseQuery(args, api, extraOptions);
    if (response.error?.status === 401) {
        // Checking whether the mutex is locked
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const refreshResult = (await baseQuery(
                    {
                        url: '/auth/v1/refresh',
                        method: 'POST',
                        body: {},
                    },
                    api,
                    extraOptions,
                )) as { data: { accessToken: string; type: string } };

                if (refreshResult.data) {
                    api.dispatch(
                        authUser({
                            accessToken: refreshResult.data.accessToken,
                            type: refreshResult.data.type,
                        }),
                    );

                    // Retry the original query with new access token
                    response = await baseQuery(args, api, extraOptions);
                } else {
                    api.dispatch(userLogout());
                }
            } finally {
                // Release the mutex
                release();
            }
        } else {
            // If the mutex is locked, wait for it to be unlocked and then retry the query
            await mutex.waitForUnlock();
            response = await baseQuery(args, api, extraOptions);
        }
    } else if (response.error) {
        captureException(response.error);
    }
    return response;
};

export const api = createApi({
    baseQuery: baseQueryWithReAuth,
    reducerPath: 'apiPath',
    tagTypes: [
        'buildings',
        'meters',
        'readings',
        'contractors',
        'users',
        'houseManagement',
        'communityOwners',
        'user',
    ],
    endpoints: () => ({}),
});
